import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'react-bootstrap';
import { debounce } from 'lodash';
import { toasterMsg } from '../../Toaster/Toaster';
import GoogleMapContext from '../../GoogleMapComponent/GoogleMapContext';
import Loader from '../../Utility/Loader/Loader';

const placeDetailsFields = [
  'name',
  'formatted_address',
  'geometry',
  'place_id',
  'rating',
  'business_status',
  'reviews',
  'user_ratings_total',
  'opening_hours',
  'formatted_phone_number',
  'website',
];

export function GoogleSetting({ obj, updateSetting }) {
  const { t } = useTranslation('common');

  const [suggestions, setSuggestions] = useState([]);
  const { googleScriptLoaded, currentCoordinates, autocompleteService, placesService } = useContext(GoogleMapContext);
  const [suggestionLoader, setSuggestionLoader] = useState(false);

  // Use useCallback to ensure the debounced function doesn't change across renders
  const debouncedGetPlacePredictions = useCallback(
    debounce((input, objClone, key, i) => {
      const request = {
        input,
      };
      autocompleteService?.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          objClone[key][i].suggestions = predictions;
          updateSetting(objClone);
        }
      });
    }, 1000),
    [autocompleteService], // Dependency array is empty to ensure this is only created once
  );

  const handleInputChange = (e, i, key) => {
    const input = e.target.value;
    const objClone = { ...obj };
    if (i >= 0) {
      objClone[key][i].address = input;
    }
    updateSetting(objClone);

    if (input?.length > 2 && window.google) {
      debouncedGetPlacePredictions(input, objClone, key, i);
    } else {
      objClone[key][i].suggestions = [];
    }
  };

  const handleSelectSuggestion = (place, i, key) => {
    const objClone = { ...obj };
    if (i >= 0) {
      objClone[key][i].address = place.description;
      objClone[key][i].suggestions = [];
    }
    updateSetting(objClone);
    const request = {
      placeId: place.place_id,
      fields: placeDetailsFields,
    };
    placesService?.getDetails(request, (placeDetails, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        objClone[key][i].placeDetails = placeDetails;
        objClone[key][i].lat = placeDetails.geometry.location.lat();
        objClone[key][i].lng = placeDetails.geometry.location.lng();
      }
      updateSetting(objClone);
    });
  };

  const onChange = (value, key, i) => {
    const objClone = { ...obj };
    if (key === 'isAddressType') {
      objClone.addresses = [{ address: '', lat: 0, lng: 0, placeDetails: null, suggestions: [] }];
      objClone.placesNearMe = '';
      objClone.relatedPlacesNearMe = [];
    }
    if (i >= 0) {
      objClone[key][i].address = value;
    } else {
      objClone[key] = value;
    }
    updateSetting(objClone);
  };

  const addRemoveAddress = (i) => {
    const objClone = { ...obj };
    if (i >= 0) {
      objClone?.addresses?.splice(i, 1);
    } else {
      objClone.addresses.push({ address: '', lat: 0, lng: 0, placeDetails: null, suggestions: [] });
    }
    updateSetting(objClone);
  };

  const handleManualAddressEntry = (e, i, key, address) => {
    if (e.key === 'Enter') {
      const objClone = { ...obj };
      const request = {
        query: address,
        fields: ['place_id', 'name', 'formatted_address', 'geometry'],
      };

      placesService?.findPlaceFromQuery(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.length > 0) {
          const placeId = results[0].place_id;
          // Fetch detailed information for the first result
          const detailsRequest = {
            placeId,
            fields: placeDetailsFields,
          };
          placesService?.getDetails(detailsRequest, (placeDetails, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              const { location } = results[0].geometry;
              objClone[key][i].placeDetails = placeDetails;
              objClone[key][i].lat = location.lat();
              objClone[key][i].lng = location.lng();
              objClone[key][i].suggestions = [];
            }
          });
        }
        updateSetting(objClone);
      });
    }
  };

  function appendNearMe(input) {
    const searchTerm = input.trim().toLowerCase(); // Convert input to lowercase for comparison
    if (searchTerm.includes('near me')) {
      return input; // Return the original input if it already contains "near me"
    }

    return `${input} near me`; // Otherwise, concatenate "near me"
  }

  // Debounced function for calling the nearby search API
  const debouncedNearbySearch = useCallback(
    debounce((value) => {
      const locate = currentCoordinates?.lat && currentCoordinates.lng ? currentCoordinates : { lat: 20.3685888, lng: 72.9088 };
      console.log('🚀 ~ GoogleSetting ~ locate:', locate);
      const request = {
        location: new window.google.maps.LatLng(locate.lat, locate.lng), // Provide a default location or use user's location
        radius: 5000, // Adjust the radius as needed
        keyword: appendNearMe(value),
      };
      placesService.nearbySearch(request, (results, status) => {
        console.log('🚀 ~ GoogleSetting ~ results:', results);
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const first10Results = results?.slice(0, 10);
          console.log('🚀 ~ GoogleSetting ~ first10Results:', first10Results);
          setSuggestions(first10Results);
          setSuggestionLoader(false);
        } else {
          setSuggestionLoader(false);
        }
      });
    }, 1000),
    [placesService], // Empty dependency array to prevent re-creating the debounced function
  );

  const handlePlacesNearMeChange = (value) => {
    if (value.length > 2 && placesService) {
      setSuggestionLoader(true);
      setSuggestions([]);
      debouncedNearbySearch(value); // Call debounced function here
    }
  };

  const handlePlaceValChange = (value) => {
    const objClone = { ...obj };
    objClone.placesNearMe = value;
    updateSetting(objClone);
  };

  useEffect(() => {
    if (obj?.placesNearMe) {
      handlePlacesNearMeChange(obj?.placesNearMe);
    }
  }, [obj?.placesNearMe]);

  const handleSuggestisonSelect = async (place) => {
    const lat = place.geometry.location.lat(); // Get latitude
    const lng = place.geometry.location.lng(); // Get longitude
    try {
      const objClone = { ...obj };
      objClone.placesNearMe = place?.name || obj.placesNearMe;
      const currlocation = lat && lng ? { lat, lng } : { lat: 20.3685888, lng: 72.9088 };
      const request = {
        location: new window.google.maps.LatLng(currlocation.lat, currlocation.lng),
        radius: 5000, // Adjust radius as needed
        keyword: appendNearMe(place?.name), // Change the type to whatever you want to search for
      };
      console.log('🚀 ~ handleSuggestisonSelect ~ place?.name:', place?.name);
      placesService?.nearbySearch(request, async (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const first10Results = results?.slice(0, 10);
          const getAllPlaceIDs = first10Results?.map((vl) => vl?.place_id);
          if (getAllPlaceIDs?.length > 0) {
            const placeDetailsPromises = getAllPlaceIDs.map((placeId) => {
              return new Promise((resolve, reject) => {
                placesService?.getDetails({ placeId }, (placeDetails, status) => {
                  if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    resolve(placeDetails);
                  } else {
                    reject(status);
                  }
                });
              });
            });
            // Fetch all place details using Promise.all
            try {
              const placeDetailsArray = await Promise.all(placeDetailsPromises);
              objClone.relatedPlacesNearMe = placeDetailsArray || [];
            } catch (error) {
              toasterMsg(`Fetching place details failed: ${error}`, 'danger');
              setSuggestionLoader(false);
            }
          } else {
            objClone.relatedPlacesNearMe = [];
          }
          updateSetting(objClone);
        } else {
          setSuggestionLoader(false);
        }
      });
    } catch (error) {
      console.error('Error fetching nearby places:', error);
    }
  };

  const handlePlacesNearEnter = (e) => {
    if (e.key === 'Enter') {
      const objClone = { ...obj };
      const first10Results = suggestions?.slice(0, 10);
      objClone.relatedPlacesNearMe = first10Results;
      updateSetting(objClone);
    }
  };

  const renderSuggestions = () => {
    if (suggestionLoader) {
      return (
        <ul>
          <Loader />
        </ul>
      );
    }
    if (suggestions?.length) {
      return (
        <ul>
          {suggestions.map((suggestion) => (
            <li key={suggestion?.place_id} onClick={() => handleSuggestisonSelect(suggestion)}>
              {suggestion.name}
            </li>
          ))}
        </ul>
      );
    }

    if (!suggestionLoader && obj?.placesNearMe?.length > 3 && suggestions?.length === 0) {
      return (
        <ul>
          <li className="text-center">No Data</li>
        </ul>
      );
    }

    return null;
  };
  return (
    <>
      <div className="common-input-control">
        <p className="field-name">{t('DPP.CONTROL.GOOGLE.TITLE')}</p>
        <input
          type="text"
          className="form-control"
          value={obj?.title}
          placeholder={t('DPP.CONTROL.GOOGLE.ENTER_TITLE')}
          onChange={(e) => onChange(e.target.value, 'title')}
          maxLength="40"
        />
      </div>

      <FormGroup>
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center">
            <p className="custom-radio mb-0">
              <input
                type="radio"
                id="addresses"
                value="addresses"
                onChange={(e) => onChange(e.target.value, 'isAddressType')}
                name="addresses"
                checked={obj?.isAddressType === 'addresses'}
              />
              <label className="text-black text-bold" htmlFor="addresses">
                {t('DPP.CONTROL.GOOGLE.ADDRESSES')}
              </label>
            </p>
            <button type="button" aria-label="Upload" className="btn btn-outline sm ml-3" onClick={() => addRemoveAddress()}>
              <i className="icon-add" />
            </button>
          </div>
          <p className="custom-radio mb-0 ml-4">
            <input
              type="radio"
              id="nearPlaces"
              value="nearPlaces"
              onChange={(e) => onChange(e.target.value, 'isAddressType')}
              name="nearPlaces"
              checked={obj?.isAddressType === 'nearPlaces'}
            />
            <label className="text-black text-bold" htmlFor="nearPlaces">
              {' '}
              {t('DPP.CONTROL.GOOGLE.PLACES_NEAR_ME')}
            </label>
          </p>
        </div>
      </FormGroup>
      {obj?.isAddressType === 'addresses' &&
        obj?.addresses?.map((add, i) => {
          return (
            <FormGroup key={i}>
              <div className="location-list">
                {googleScriptLoaded ? (
                  <div>
                    <input
                      type="text"
                      value={add.address}
                      onChange={(e) => handleInputChange(e, i, 'addresses')}
                      placeholder={t('DPP.CONTROL.GOOGLE.TYPE_AN_ADDRESS')}
                      className="form-control f-16"
                      onKeyPress={(e) => handleManualAddressEntry(e, i, 'addresses', add.address)} // Handle manual entry on Enter
                    />
                    {add?.suggestions?.length > 0 && (
                      <ul
                        style={{
                          listStyleType: 'none',
                          padding: 0,
                          border: '1px solid #ccc',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        {add.suggestions.map((suggestion) => (
                          <li
                            key={suggestion.place_id}
                            onClick={() => handleSelectSuggestion(suggestion, i, 'addresses')}
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #ddd',
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSelectSuggestion(suggestion, i, 'addresses');
                              }
                            }}
                          >
                            {suggestion.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div>Loading...</div>
                )}
                {obj?.addresses?.length > 1 && (
                  <button type="button" aria-label="Upload" className="btn btn-outline btn-absolute" onClick={() => addRemoveAddress(i)}>
                    <i className="icon-Delete-bold" />
                  </button>
                )}
              </div>
            </FormGroup>
          );
        })}
      {obj?.isAddressType === 'nearPlaces' && (
        <FormGroup>
          {googleScriptLoaded ? (
            <div className="location-list">
              <div className="d-flex align-items-end">
                <input
                  type="text"
                  value={obj?.placesNearMe}
                  onChange={(e) => handlePlaceValChange(e.target.value)}
                  placeholder="Search for places"
                  className="form-control f-16"
                  onKeyDown={(e) => handlePlacesNearEnter(e)} // Handle manual entry on Enter
                />
                <span className="text-nowrap ml-2">Near Me</span>
              </div>
              {renderSuggestions()}
            </div>
          ) : null}
        </FormGroup>
      )}
    </>
  );
}

export default GoogleSetting;
